import { useUser } from '@clerk/clerk-react'
import { BrainLogo } from '@/components/ui/brain-logo'
import { Button } from '@/components/ui/button'
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react'
import { SlideIn, FadeIn, ScaleIn } from '@/components/motion'
import { ClientBasicInfoForm } from './ClientBasicInfoForm'
import { FreelancerBasicInfoForm } from './FreelancerBasicInfoForm'
import { getVideoEmbedInfo } from '@/lib/utils/video'

interface BasicInfoStepProps {
  selectedRole: 'genius' | 'client'
  user: ReturnType<typeof useUser>['user'] | null | undefined
  formData: {
    display_name: string
    headline: string
    location: string
    freelancer_bio: string
    company_name: string
    company_size: string
    industry: string
    client_website_url: string
    client_location: string
    client_bio: string
    freelancer_email: string
    client_email: string
    profile_picture_url: string | null
    video_intro_url: string
  }
  isSubmitting: boolean
  onBack: () => void
  onCompanyNameChange: (value: string) => void
  onClientBioChange: (value: string) => void
  onCompanySizeChange?: (value: string) => void
  onIndustryChange?: (value: string) => void
  onLocationChange: (value: string) => void
  onClientLocationChange?: (value: string) => void
  onWebsiteUrlChange?: (value: string) => void
  onHeadlineChange: (value: string) => void
  onFreelancerBioChange: (value: string) => void
  onVideoIntroUrlChange: (value: string) => void
  onImageUploaded: (imageUrl: string) => void
  onImageRemoved: () => void
  onSubmit: () => void
}

export function BasicInfoStep({
  selectedRole,
  user,
  formData,
  isSubmitting,
  onBack,
  onCompanyNameChange,
  onClientBioChange,
  onCompanySizeChange,
  onIndustryChange,
  onClientLocationChange,
  onWebsiteUrlChange,
  onHeadlineChange,
  onFreelancerBioChange,
  onLocationChange,
  onVideoIntroUrlChange,
  onImageUploaded,
  onImageRemoved,
  onSubmit,
}: BasicInfoStepProps) {
  const isFormValid = selectedRole === 'client'
    ? formData.company_name.trim() && formData.client_bio.trim() && formData.client_bio.trim().length >= 100 && formData.client_bio.trim().length <= 1500
    : formData.headline.trim()
      && formData.location.trim()
      && formData.freelancer_bio.trim()
      && formData.freelancer_bio.trim().length >= 100
      && formData.freelancer_bio.trim().length <= 1500
      && formData.video_intro_url?.trim()
      && getVideoEmbedInfo(formData.video_intro_url).type !== null

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <ScaleIn className="w-full max-w-[600px]">
        <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
          {/* Header with gradient */}
          <div
            className="pt-12 px-8 pb-8 flex flex-col items-center"
            style={{
              background: 'linear-gradient(180deg, rgba(248, 249, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)',
            }}
          >
            <FadeIn delay={0.1}>
              <BrainLogo size="md" variant="light" showGlow={false} />
            </FadeIn>
            <SlideIn direction="up" delay={0.2}>
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 text-center mt-4">
                {selectedRole === 'client' ? 'Tell us about your company' : 'Tell us about yourself'}
              </h1>
            </SlideIn>
            <SlideIn direction="up" delay={0.3}>
              <p className="text-lg text-slate-500 text-center mt-2">
                Let's start with some basic information
              </p>
            </SlideIn>
          </div>

          {/* Form */}
          <div className="px-8 pb-12">
            <SlideIn direction="up" delay={0.4}>
              <div className="space-y-6">
                {selectedRole === 'client' ? (
                  <ClientBasicInfoForm
                    user={user}
                    companyName={formData.company_name}
                    clientBio={formData.client_bio}
                    clientEmail={formData.client_email}
                    profilePictureUrl={formData.profile_picture_url}
                    companySize={formData.company_size}
                    industry={formData.industry}
                    location={formData.client_location}
                    websiteUrl={formData.client_website_url}
                    onCompanyNameChange={onCompanyNameChange}
                    onClientBioChange={onClientBioChange}
                    onCompanySizeChange={onCompanySizeChange}
                    onIndustryChange={onIndustryChange}
                    onLocationChange={onClientLocationChange}
                    onWebsiteUrlChange={onWebsiteUrlChange}
                    onImageUploaded={onImageUploaded}
                    onImageRemoved={onImageRemoved}
                  />
                ) : (
                  <FreelancerBasicInfoForm
                    user={user}
                    displayName={formData.display_name}
                    headline={formData.headline}
                    location={formData.location}
                    freelancerBio={formData.freelancer_bio}
                    freelancerEmail={formData.freelancer_email}
                    profilePictureUrl={formData.profile_picture_url}
                    videoIntroUrl={formData.video_intro_url}
                    onHeadlineChange={onHeadlineChange}
                    onFreelancerBioChange={onFreelancerBioChange}
                    onLocationChange={onLocationChange}
                    onVideoIntroUrlChange={onVideoIntroUrlChange}
                    onImageUploaded={onImageUploaded}
                    onImageRemoved={onImageRemoved}
                  />
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-4 pt-4">
                  <Button
                    variant="outline"
                    onClick={onBack}
                    className="flex-1 rounded-full h-12"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={onSubmit}
                    disabled={isSubmitting || !isFormValid}
                    className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </SlideIn>
          </div>
        </div>
      </ScaleIn>
    </div>
  )
}
